import moment from 'moment';

import type {
  IWorkJourneyMatrixCell,
  TWorkJourneyMatrixCellCode,
  TWorkJourneyMatrixCellKind,
} from '@src/models/interfaces/workJourneyManagement.interface';

import { getMatrixTodayIsoDate } from './isFutureMatrixCalendarDate';
import { isReplacementCell } from './isReplacementCell';
import type { MatrixExceptionDateRange } from './resolveMatrixExceptionDateRanges';

/**
 * Exception create/delete updates land via triggers + ES, so a matrix reload
 * right after save often returns stale chips. These helpers patch the loaded
 * cells so the grid updates immediately (same idea as attendance edit optimism).
 */

type ExceptionMappedCell = {
  kind: TWorkJourneyMatrixCellKind;
  code: TWorkJourneyMatrixCellCode;
};

const MATRIX_PLANNED_CELL: ExceptionMappedCell = { kind: 'planned', code: 'WORK' };
const MATRIX_PRESENT_CELL: ExceptionMappedCell = { kind: 'work', code: 'PRE' };

const CODE_BY_EXCEPTION_TYPE: Record<string, ExceptionMappedCell> = {
  unjustified_absence: { kind: 'unjustified_absence', code: 'AUI' },
  justified_absence: { kind: 'justified_absence', code: 'AUJ' },
  vacation: { kind: 'vacation', code: 'VAC' },
  medical_leave: { kind: 'medical_leave', code: 'LM' },
  paid_leave: { kind: 'paid_leave', code: 'PGR' },
  unpaid_leave: { kind: 'unpaid_leave', code: 'PSG' },
  prenatal: { kind: 'prenatal', code: 'PREN' },
  postnatal: { kind: 'postnatal', code: 'POSTN' },
  shift_replacement: { kind: 'replacement', code: 'REM' },
};

export const mapMatrixExceptionTypeToCell = (
  exceptionType?: string | null,
): ExceptionMappedCell => {
  if (!exceptionType) return MATRIX_PLANNED_CELL;
  return CODE_BY_EXCEPTION_TYPE[exceptionType] ?? { kind: 'other_absence', code: 'ABS' };
};

/** Expand inclusive YYYY-MM-DD ranges into discrete calendar dates. */
export const expandMatrixExceptionRangesToDates = (
  ranges: MatrixExceptionDateRange[],
): string[] => {
  const dates = new Set<string>();
  ranges.forEach(range => {
    const start = moment(range.startDate, 'YYYY-MM-DD', true);
    const end = moment(range.endDate || range.startDate, 'YYYY-MM-DD', true);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return;
    const cursor = start.clone();
    while (cursor.isSameOrBefore(end, 'day')) {
      dates.add(cursor.format('YYYY-MM-DD'));
      cursor.add(1, 'day');
    }
  });
  return Array.from(dates).sort();
};

/**
 * True while the scheduled shift has not ended — AUI must wait until then.
 * Mirrors services `isBeforeScheduledEnd`.
 */
const isBeforeScheduledEnd = (params: {
  scheduledEndAtSeconds?: number | null;
  nowSeconds?: number | null;
  date: string;
  todayIso: string;
}): boolean => {
  const nowSeconds = params.nowSeconds ?? Math.floor(Date.now() / 1000);
  if (params.scheduledEndAtSeconds != null) {
    return nowSeconds < params.scheduledEndAtSeconds;
  }
  return params.date >= params.todayIso;
};

/**
 * Mirrors backend `resolveAttendanceRecordCell` so undo restores the chip the
 * matrix would show once the exception (and AR leave) are gone.
 */
export const resolveMatrixCellAfterClearingException = (params: {
  leaveType?: string | null;
  attendanceComplete?: boolean | null;
  hasActualStart?: boolean | null;
  date: string;
  todayIso: string;
  scheduledEndAtSeconds?: number | null;
  nowSeconds?: number | null;
}): ExceptionMappedCell => {
  const leaveType =
    typeof params.leaveType === 'string' ? params.leaveType.trim() : params.leaveType || '';
  const isComplete = params.attendanceComplete === true;
  const isPastOrToday = params.date <= params.todayIso;
  const isClosedDay = params.date < params.todayIso;
  const shiftStillOpen = isBeforeScheduledEnd({
    scheduledEndAtSeconds: params.scheduledEndAtSeconds,
    nowSeconds: params.nowSeconds,
    date: params.date,
    todayIso: params.todayIso,
  });

  if (leaveType === 'justified_absence') {
    return { kind: 'justified_absence', code: 'AUJ' };
  }

  if (!isPastOrToday) {
    return MATRIX_PLANNED_CELL;
  }

  if (isComplete) {
    return MATRIX_PRESENT_CELL;
  }

  if (leaveType === 'unjustified_absence') {
    return shiftStillOpen
      ? MATRIX_PLANNED_CELL
      : { kind: 'unjustified_absence', code: 'AUI' };
  }

  if (isClosedDay || !shiftStillOpen) {
    return params.hasActualStart === true
      ? MATRIX_PRESENT_CELL
      : { kind: 'unjustified_absence', code: 'AUI' };
  }

  return MATRIX_PLANNED_CELL;
};

const leaveTypeForException = (exceptionType: string): string | null => {
  if (exceptionType === 'justified_absence') return 'justified_absence';
  if (exceptionType === 'unjustified_absence') return 'unjustified_absence';
  return null;
};

export const applyExceptionToMatrixCell = (
  cell: IWorkJourneyMatrixCell,
  params: {
    exceptionType: string;
    description?: string;
    exceptionId?: string;
    exceptionStartDate?: string;
    exceptionEndDate?: string;
  },
): IWorkJourneyMatrixCell => {
  const mapped = mapMatrixExceptionTypeToCell(params.exceptionType);
  const leaveType = leaveTypeForException(params.exceptionType);

  return {
    ...cell,
    kind: mapped.kind,
    code: mapped.code,
    exceptionId: params.exceptionId ?? cell.exceptionId,
    exceptionType: params.exceptionType,
    exceptionStartDate: params.exceptionStartDate ?? cell.exceptionStartDate,
    exceptionEndDate: params.exceptionEndDate ?? cell.exceptionEndDate,
    description: params.description || cell.description,
    leaveType,
    observation: params.exceptionType,
    attendanceCompleted: false,
    fromExceptionOnly: !cell.attendanceRecordId || cell.fromExceptionOnly === true,
    attendance: cell.attendance
      ? {
          ...cell.attendance,
          leaveType,
          observations: params.exceptionType,
          attendanceComplete: false,
          hasActualStart: false,
        }
      : {
          leaveType,
          observations: params.exceptionType,
          attendanceComplete: false,
          hasActualStart: false,
        },
  };
};

export const applyExceptionToMatrixCells = (
  cells: IWorkJourneyMatrixCell[],
  params: {
    userId: string;
    dates: string[];
    exceptionType: string;
    description?: string;
    exceptionId?: string;
    exceptionStartDate?: string;
    exceptionEndDate?: string;
  },
): IWorkJourneyMatrixCell[] => {
  const dateSet = new Set(params.dates);
  if (dateSet.size === 0) return cells;

  const existingDates = new Set<string>();
  const next = cells.map(cell => {
    if (cell.userId !== params.userId || !dateSet.has(cell.date)) return cell;
    existingDates.add(cell.date);
    return applyExceptionToMatrixCell(cell, params);
  });

  params.dates.forEach(date => {
    if (existingDates.has(date)) return;
    const mapped = mapMatrixExceptionTypeToCell(params.exceptionType);
    const leaveType = leaveTypeForException(params.exceptionType);
    next.push({
      userId: params.userId,
      date,
      kind: mapped.kind,
      code: mapped.code,
      exceptionId: params.exceptionId,
      exceptionType: params.exceptionType,
      exceptionStartDate: params.exceptionStartDate,
      exceptionEndDate: params.exceptionEndDate,
      description: params.description,
      leaveType,
      observation: params.exceptionType,
      attendanceCompleted: false,
      fromExceptionOnly: true,
      attendanceRecordId: params.exceptionId
        ? `exception:${params.exceptionId}:${date}`
        : undefined,
    });
  });

  return next;
};

export const clearExceptionFromMatrixCell = (
  cell: IWorkJourneyMatrixCell,
  todayIso: string,
  /** Override "now" for tests (epoch seconds). */
  nowSeconds?: number | null,
): IWorkJourneyMatrixCell => {
  // Exception-only synthetic cells → restore planned work (absences sit on workdays).
  if (cell.fromExceptionOnly || cell.attendanceRecordId?.startsWith('exception:')) {
    return {
      userId: cell.userId,
      date: cell.date,
      kind: 'planned',
      code: 'WORK',
      leaveType: null,
      observation: null,
      attendanceCompleted: false,
      subjectDisplayName: cell.subjectDisplayName,
      subjectIdCard: cell.subjectIdCard,
    };
  }

  const hasActualStart = cell.attendance?.hasActualStart === true;
  const attendanceComplete =
    cell.attendanceCompleted === true || cell.attendance?.attendanceComplete === true;
  const resolved = resolveMatrixCellAfterClearingException({
    leaveType: null,
    attendanceComplete,
    hasActualStart,
    date: cell.date,
    todayIso,
    scheduledEndAtSeconds: cell.attendance?.scheduledEndAtSeconds,
    nowSeconds,
  });

  return {
    ...cell,
    ...resolved,
    exceptionId: undefined,
    exceptionType: undefined,
    exceptionStartDate: undefined,
    exceptionEndDate: undefined,
    description: undefined,
    leaveType: null,
    observation: null,
    fromExceptionOnly: undefined,
    replacedUserId: undefined,
    replacedUserDisplayName: undefined,
    replacedUserIdCard: undefined,
    replacementUserDisplayName: undefined,
    replacementUserIdCard: undefined,
    replacementJourneyId: undefined,
    replacementJourneyName: undefined,
    attendance: cell.attendance
      ? {
          ...cell.attendance,
          leaveType: null,
          observations: null,
        }
      : cell.attendance,
  };
};

/** Clears every cell tied to the deleted exception id. */
export const clearExceptionFromMatrixCells = (
  cells: IWorkJourneyMatrixCell[],
  exceptionId: string,
  todayIso: string = getMatrixTodayIsoDate(),
  nowSeconds?: number | null,
): IWorkJourneyMatrixCell[] => {
  if (!exceptionId) return cells;
  const next: IWorkJourneyMatrixCell[] = [];
  cells.forEach(cell => {
    if (cell.exceptionId !== exceptionId) {
      next.push(cell);
      return;
    }
    // Inbound REM chips (cover journey): drop synthetic/exception-only cells —
    // the substitute often is not a member of that journey.
    // Outgoing REM (`replacement_out` on the substitute's home journey) must
    // restore planned WORK instead of removing the member's day.
    if (
      cell.kind !== 'replacement_out' &&
      isReplacementCell(cell) &&
      (cell.fromExceptionOnly === true ||
        cell.attendanceRecordId?.startsWith('exception:') ||
        cell.kind === 'replacement')
    ) {
      return;
    }
    next.push(clearExceptionFromMatrixCell(cell, todayIso, nowSeconds));
  });
  return next;
};

const isoDateRangesOverlap = (
  aStart: string,
  aEnd: string,
  bStart: string,
  bEnd: string,
): boolean => aStart <= bEnd && bStart <= aEnd;

/**
 * Clears an absence/leave exception and every inbound REM that covers the same
 * absent user on an overlapping range (mirrors Services cascade delete).
 */
export const clearAbsenceAndLinkedReplacementsFromMatrixCells = (
  cells: IWorkJourneyMatrixCell[],
  params: {
    absenceExceptionId: string;
    absentUserId: string;
    startDate: string;
    endDate: string;
  },
  todayIso: string = getMatrixTodayIsoDate(),
): IWorkJourneyMatrixCell[] => {
  if (!params.absenceExceptionId || !params.absentUserId) {
    return clearExceptionFromMatrixCells(cells, params.absenceExceptionId, todayIso);
  }

  const remExceptionIds = new Set<string>();
  cells.forEach(cell => {
    if (!isReplacementCell(cell) || cell.replacedUserId !== params.absentUserId) {
      return;
    }
    if (!cell.exceptionId) return;
    const remStart = cell.exceptionStartDate || cell.date;
    const remEnd = cell.exceptionEndDate || remStart;
    if (isoDateRangesOverlap(remStart, remEnd, params.startDate, params.endDate)) {
      remExceptionIds.add(cell.exceptionId);
    }
  });

  let next = clearExceptionFromMatrixCells(cells, params.absenceExceptionId, todayIso);
  remExceptionIds.forEach(remId => {
    next = clearExceptionFromMatrixCells(next, remId, todayIso);
  });
  return next;
};
